import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import api from '../utils/axios';
import { handleApiError } from '../utils/errorHandler';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface InsuranceRep {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  department?: string;
  insurerId: string;
  status: string;
  notes?: string;
  insurer?: {
    name: string;
  };
}

interface Metrics {
  totalReps: number;
  activeReps: number;
  inactiveReps: number;
}

const InsuranceRepManagement: React.FC = (): JSX.Element => {
  const [insuranceReps, setInsuranceReps] = useState<InsuranceRep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRep, setEditingRep] = useState<InsuranceRep | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalReps: 0,
    activeReps: 0,
    inactiveReps: 0
  });

  const navigate = useNavigate();

  const fetchInsuranceReps = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: InsuranceRep[] }>('/insurance-reps', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD')
        }
      });
      if (response.data.success) {
        setInsuranceReps(response.data.data);
        calculateMetrics(response.data.data);
      } else {
        message.error('Failed to fetch insurance representatives');
      }
    } catch (error) {
      console.error('Error fetching insurance representatives:', error);
      message.error('Failed to fetch insurance representatives');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, dateRange]);

  const calculateMetrics = (data: InsuranceRep[]) => {
    setMetrics({
      totalReps: data.length,
      activeReps: data.filter(rep => rep.status === 'ACTIVE').length,
      inactiveReps: data.filter(rep => rep.status === 'INACTIVE').length
    });
  };

  useEffect(() => {
    fetchInsuranceReps();
  }, [fetchInsuranceReps]);

  const handleEdit = (rep: InsuranceRep) => {
    setEditingRep(rep);
    setIsModalVisible(true);
    form.setFieldsValue(rep);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/insurance-reps/${id}`);
      message.success('Insurance representative deleted successfully');
      fetchInsuranceReps();
    } catch (error) {
      console.error('Error deleting insurance representative:', error);
      message.error('Failed to delete insurance representative');
    }
  };

  const handleCreate = () => {
    setEditingRep(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRep) {
        await api.put(`/insurance-reps/${editingRep.id}`, values);
        message.success('Insurance representative updated successfully');
      } else {
        await api.post('/insurance-reps', values);
        message.success('Insurance representative created successfully');
      }
      setIsModalVisible(false);
      fetchInsuranceReps();
    } catch (error) {
      console.error('Error saving insurance representative:', error);
      message.error('Failed to save insurance representative');
    }
  };

  const columns: ColumnsType<InsuranceRep> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      sorter: (a, b) => {
        const aName = `${a.firstName} ${a.lastName}`;
        const bName = `${b.firstName} ${b.lastName}`;
        return aName.localeCompare(bName);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Insurance Company',
      dataIndex: ['insurer', 'name'],
      key: 'insurer',
      sorter: (a, b) => {
        const aName = a.insurer?.name ?? '';
        const bName = b.insurer?.name ?? '';
        return aName.localeCompare(bName);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'success' : 'error'}>
          {status}
        </Tag>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/insurance-representatives/${record.id}`)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<InsuranceRep>['onChange'] = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(pagination);
    }

    if (sorter) {
      const { field, order } = sorter as { field: string; order: 'ascend' | 'descend' | null };
      const sortedData = [...insuranceReps].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Handle special cases
        if (field === 'name') {
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
        } else if (field === 'insurer') {
          aValue = a.insurer?.name ?? '';
          bValue = b.insurer?.name ?? '';
        } else {
          aValue = a[field as keyof InsuranceRep] ?? '';
          bValue = b[field as keyof InsuranceRep] ?? '';
        }

        if (order === 'ascend') {
          return String(aValue).localeCompare(String(bValue));
        }
        if (order === 'descend') {
          return String(bValue).localeCompare(String(aValue));
        }
        return 0;
      });
      setInsuranceReps(sortedData);
    }
  };

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Representatives"
              value={metrics.totalReps}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active Representatives"
              value={metrics.activeReps}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Inactive Representatives"
              value={metrics.inactiveReps}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Insurance Representatives</Title>
          <Space>
            <Input
              placeholder="Search representatives..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Representative
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={insuranceReps}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
        />
      </Card>

      <Modal
        title={editingRep ? 'Edit Representative' : 'New Representative'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="title"
              label="Title"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="department"
              label="Department"
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            name="insurerId"
            label="Insurance Company"
            rules={[{ required: true, message: 'Please select insurance company' }]}
          >
            <Select>
              {insuranceReps.map((rep) => (
                <Option key={rep.insurerId} value={rep.insurerId}>
                  {rep.insurer?.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value="ACTIVE">Active</Option>
              <Option value="INACTIVE">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InsuranceRepManagement; 